import { useState } from 'react';
import {
  Paper,
  Title,
  Stack,
  Text,
  Group,
  Button,
  Table,
  ActionIcon,
  Badge,
  Modal,
  TextInput,
  FileInput,
  Loader,
  Center,
  Select,
} from '@mantine/core';
import {
  IconUpload,
  IconDownload,
  IconTrash,
  IconFileText,
  IconPhoto,
  IconFileTypePdf,
  IconFile,
  IconEye,
} from '@tabler/icons-react';
import { format } from 'date-fns';
import { notifications } from '@mantine/notifications';
import { useAssetDocuments, useUploadDocument, useDeleteDocument, getDocumentDownloadUrl } from '@/hooks/api/useDocuments';
import { useDocumentTypes } from '@/hooks/api/useDocumentTypes';
import { useAuth } from '@/hooks/useAuth';

interface AssetDocumentsProps {
  organizationId: string;
  assetId: string;
}

const getFileIcon = (fileType: string) => {
  if (fileType.startsWith('image/')) return <IconPhoto size={20} />;
  if (fileType === 'application/pdf') return <IconFileTypePdf size={20} />;
  if (fileType.startsWith('text/')) return <IconFileText size={20} />;
  return <IconFile size={20} />;
};

const getFileTypeColor = (fileType: string) => {
  if (fileType.startsWith('image/')) return 'blue';
  if (fileType === 'application/pdf') return 'red';
  if (fileType.startsWith('text/')) return 'green';
  return 'gray';
};

const formatFileSize = (bytes: number | null) => {
  if (!bytes) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function AssetDocuments({ organizationId, assetId }: AssetDocumentsProps) {
  const { user } = useAuth();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<string>('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [documentTypeId, setDocumentTypeId] = useState<string | null>(null);

  const { data: documents = [], isLoading } = useAssetDocuments(organizationId, assetId);
  const { data: documentTypes = [] } = useDocumentTypes(organizationId);
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument();

  // Prepare document type options for select
  const documentTypeOptions = documentTypes.map((dt) => ({
    value: dt.id,
    label: dt.name,
  }));

  const handleUpload = async () => {
    if (!file || !description.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please provide both a file and description',
        color: 'red',
      });
      return;
    }

    if (!user?.id) {
      notifications.show({
        title: 'Error',
        message: 'User not authenticated',
        color: 'red',
      });
      return;
    }

    try {
      await uploadMutation.mutateAsync({
        organizationId,
        assetId,
        file,
        description,
        userId: user.id,
        documentTypeId: documentTypeId || undefined,
      });

      notifications.show({
        title: 'Success',
        message: 'Document uploaded successfully',
        color: 'green',
      });

      setUploadModalOpen(false);
      setFile(null);
      setDescription('');
      setDocumentTypeId(null);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to upload document',
        color: 'red',
      });
    }
  };

  const handleDelete = async (documentId: string, fileUrl: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      await deleteMutation.mutateAsync({
        documentId,
        organizationId,
        assetId,
        fileUrl,
      });

      notifications.show({
        title: 'Success',
        message: 'Document deleted successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete document',
        color: 'red',
      });
    }
  };

  const handlePreview = async (fileUrl: string, fileType: string) => {
    try {
      const signedUrl = await getDocumentDownloadUrl(fileUrl);
      setPreviewUrl(signedUrl);
      setPreviewType(fileType);
      setPreviewModalOpen(true);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load document preview',
        color: 'red',
      });
    }
  };

  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      const signedUrl = await getDocumentDownloadUrl(fileUrl);
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to download document',
        color: 'red',
      });
    }
  };

  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Center h={200}>
          <Loader />
        </Center>
      </Paper>
    );
  }

  return (
    <>
      <Paper p="md" withBorder>
        <Stack gap="md">
          <Group justify="apart">
            <div>
              <Title order={3}>Documents</Title>
              <Text size="sm" c="dimmed">
                Upload and manage documents related to this asset
              </Text>
            </div>
            <Button
              leftSection={<IconUpload size={16} />}
              onClick={() => setUploadModalOpen(true)}
            >
              Upload Document
            </Button>
          </Group>

          {documents.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No documents uploaded yet
            </Text>
          ) : (
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>File</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Format</Table.Th>
                  <Table.Th>Size</Table.Th>
                  <Table.Th>Uploaded</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {documents.map((doc) => (
                  <Table.Tr key={doc.id}>
                    <Table.Td>
                      <Group gap="xs">
                        {getFileIcon(doc.file_type)}
                        <Text size="sm">{doc.file_name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{doc.description}</Text>
                    </Table.Td>
                    <Table.Td>
                      {doc.document_type ? (
                        <Badge size="sm" variant="light" color={doc.document_type.color || 'gray'}>
                          {doc.document_type.name}
                        </Badge>
                      ) : (
                        <Text size="sm" c="dimmed">-</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="outline" color={getFileTypeColor(doc.file_type)}>
                        {doc.file_type.split('/')[1]?.toUpperCase() || 'FILE'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {formatFileSize(doc.file_size)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {format(new Date(doc.uploaded_at), 'MMM dd, yyyy')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => handlePreview(doc.file_url, doc.file_type)}
                          title="Preview"
                        >
                          <IconEye size={18} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="green"
                          onClick={() => handleDownload(doc.file_url, doc.file_name)}
                          title="Download"
                        >
                          <IconDownload size={18} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleDelete(doc.id, doc.file_url)}
                          title="Delete"
                        >
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Paper>

      {/* Upload Modal */}
      <Modal
        opened={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        title="Upload Document"
      >
        <Stack gap="md">
          <FileInput
            label="File"
            placeholder="Select a file"
            value={file}
            onChange={setFile}
            required
            accept="image/*,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx"
          />
          <TextInput
            label="Description"
            placeholder="Enter document description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
          <Select
            label="Document Type"
            placeholder="Select a category"
            data={documentTypeOptions}
            value={documentTypeId}
            onChange={setDocumentTypeId}
            clearable
            searchable
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setUploadModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              loading={uploadMutation.isPending}
              leftSection={<IconUpload size={16} />}
            >
              Upload
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Preview Modal */}
      <Modal
        opened={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPreviewUrl(null);
          setPreviewType('');
        }}
        title="Document Preview"
        size="xl"
      >
        {previewUrl && (
          <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
            {previewType.startsWith('image/') && (
              <img
                src={previewUrl}
                alt="Document preview"
                style={{ width: '100%', height: 'auto' }}
              />
            )}
            {previewType === 'application/pdf' && (
              <iframe
                src={previewUrl}
                title="PDF preview"
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
            )}
            {previewType.startsWith('text/') && (
              <iframe
                src={previewUrl}
                title="Text preview"
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
            )}
            {!previewType.startsWith('image/') &&
              previewType !== 'application/pdf' &&
              !previewType.startsWith('text/') && (
                <Text c="dimmed" ta="center" py="xl">
                  Preview not available for this file type. Please download to view.
                </Text>
              )}
          </div>
        )}
      </Modal>
    </>
  );
}
